import { useContext, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import CustomSelect from "../../common/customSelect/customSelect";
import companyBasedRequest from "../../../request/companyBasedRequest";
import scheduleBasedRequest from "../../../request/scheduleBasedRequest";
import translations from "../../../translations/translations";
import "./addBookingPopup.css";
import { CombinedContext } from "../../../context/combinedContext";

const AddBookingPopup = ({ staff, date, startTime, services, maxMinutes, token, onClose, onSuccess }) => {
    const popRef = useRef(null);

    const { userData, setUserData, language } = useContext(CombinedContext);

    const [mode, setMode] = useState("booking"); // booking | block
    const [selectedServices, setSelectedServices] = useState([]);
    const [textValue, setTextValue] = useState(""); // customerName OR block reason
    const [duration, setDuration] = useState(null);
    const [saving, setSaving] = useState(false);
    const [popupHeight, setPopupHeight] = useState(0);
    const [localStartTime, setLocalStartTime] = useState(startTime);

    const totalServiceDuration = selectedServices.reduce(
        (sum, s) => sum + (s.duration || 0),
        0
    );
    
    const remainingMinutes = maxMinutes - totalServiceDuration;
    const finalDuration = duration || totalServiceDuration || 30;
    

    // Measure popup height for correct anchoring
    useEffect(() => {
        if (popRef.current) {
            setPopupHeight(popRef.current.offsetHeight);
        }
    }, []);

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (!popRef.current?.contains(e.target)) onClose();
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [onClose]);

    const start = dayjs(`${date}T${localStartTime}`);
    const end = start.add(finalDuration, "minute");

    function addMinutesToHHMM(time, minutes) {
        const [h, m] = time.split(":").map(Number);
        const total = h * 60 + m + minutes;

        const hh = String(Math.floor(total / 60)).padStart(2, "0");
        const mm = String(total % 60).padStart(2, "0");

        return `${hh}:${mm}`;
    }

    async function handleConfirm() {
        try {
            setSaving(true);

            const startDayjs = dayjs(`${date}T${localStartTime}`);
            const endDayjs = startDayjs.add(finalDuration, "minute");

            if (mode === "booking") {

                if (selectedServices.length === 0) {
                    setSaving(false);
                    return;
                }
            
                const startDayjs = dayjs(`${date}T${localStartTime}`);
                const endDayjs = startDayjs.add(finalDuration, "minute");                
            
                await companyBasedRequest.manualBooking(
                    {
                        shopId: userData.user.shopId,
                        // shopName: userData.shop.shopName,
            
                        staffId: staff.staffId,
                        staffName: `${staff.firstName} ${staff.lastName}`,
            
                        startTime: startDayjs.format("YYYY-MM-DDTHH:mm:ss"),
                        endTime: endDayjs.format("YYYY-MM-DDTHH:mm:ss"),
            
                        services: selectedServices.map((s) => ({
                            serviceId: s.serviceId,
                            serviceNameEn: s.serviceNameEn || s.nameEn || s.name,
                            serviceNameEs: s.serviceNameEs || s.nameEs || s.name,
                            duration: s.duration || 0,
                            isHomeService: s.isHomeService === true,
                        })),
            
                        customerName: textValue || "Walk-In",
                        notes: null
                    },
                    token
                );
            }

            if (mode === "block") {
                const startHHMM = localStartTime;
                const endHHMM = addMinutesToHHMM(localStartTime, finalDuration);

                await scheduleBasedRequest.addScheduleExclusion(
                    staff.scheduleId,
                    {
                        startDate: date,
                        endDate: date,
                        reason: textValue || "Blocked",
                        blocks: [{ start: startHHMM, end: endHHMM }],
                    },
                    token
                );
            }

            onSuccess();
            onClose();
        } finally {
            setSaving(false);
        }
    }

    function buildTimeOptions(baseTime) {
        const base = dayjs(`${date}T${baseTime}`);
        const options = [];

        // ±1 hour around click, 15-min steps
        for (let i = -4; i <= 4; i++) {
            const t = base.add(i * 15, "minute");
            options.push(t.format("HH:mm"));
        }

        return options;
    }

    const hasHomeService = selectedServices.some(s => s.isHomeService === true);
const hasInShopService = selectedServices.some(s => s.isHomeService !== true);

const lockedType = hasHomeService
    ? "home"
    : hasInShopService
    ? "shop"
    : null;

function canAddService(service) {
    if (!lockedType) return true;
    if (lockedType === "home") return service.isHomeService === true;
    return service.isHomeService !== true;
}

    const timeOptions = buildTimeOptions(startTime);

    return (
        <div className="sf-quick-overlay" onClick={onClose}>
            <div ref={popRef} className="sf-quick-pop-centered" onClick={(e) => e.stopPropagation()}>
                {/* TIME LABEL */}
                <div className="sf-quick-time">
                    <div className="sf-quick-time-left">
                        {start.format("ddd MMM D, ")}

                        <select
                            className="sf-inline-time-select"
                            value={localStartTime}
                            onChange={(e) => setLocalStartTime(e.target.value)}
                        >
                            {timeOptions.map((t) => (
                                <option key={t} value={t}>
                                    {dayjs(`${date}T${t}`).format("h:mm A")}
                                </option>
                            ))}
                        </select>
                    </div>

                    <button className="sf-quick-close" onClick={onClose} aria-label="Close">
                        ✕
                    </button>
                </div>

                {/* MODE TOGGLE */}
                <div className="sf-quick-toggle">
                    <button className={mode === "booking" ? "active" : ""} onClick={() => setMode("booking")}>
                        Walk-in
                    </button>
                    <button className={mode === "block" ? "active" : ""} onClick={() => setMode("block")}>
                        Block time
                    </button>
                </div>

                {/* ───────── WALK-IN FIELDS ───────── */}
                {mode === "booking" && (
                    <>
                        <CustomSelect
                            multi
                            value={selectedServices.map((s) => s.serviceId)}
                            onChange={(ids) => {
                                const selected = services.filter((s) => ids.includes(s.serviceId));

                                setSelectedServices(selected);
                            }}
                            placeholder={translations[language].calendar.walkin.service.placeholder}
                            options={services.map((s) => {
                                const duration = s.duration || 0;

                                const alreadySelected = selectedServices.some((ss) => ss.serviceId === s.serviceId);

                                const exceedsTime = !alreadySelected && duration > remainingMinutes;

                                const disabled = !canAddService(s) || exceedsTime;
                            
                                return {
                                    value: s.serviceId,
                                    label: (
                                        <>
                                            {s.serviceNameEn || s.nameEn || s.name || "Service"}
                                            {s.isHomeService && (
                                                <>
                                                    <span className="sf-dot"> • </span>
                                                    {language === "es" ? "A domicilio" : "Home service"}
                                                </>
                                            )}
                                            <span className="sf-dot"> • </span>
                                            {s.duration || 0} min
                                        </>
                                    ),
                                    disabled,
                                };
                            })}
                        />

                        <input
                            className="sf-quick-input"
                            placeholder={translations[language].calendar.walkin.client.label}
                            value={textValue}
                            onChange={(e) => setTextValue(e.target.value)}
                        />
                    </>
                )}

                {/* ───────── BLOCK FIELDS ───────── */}
                {mode === "block" && (
                    <input
                        className="sf-quick-input"
                        placeholder={language === "es" ? "Motivo (opcional)" : "Reason (optional)"}
                        value={textValue}
                        onChange={(e) => setTextValue(e.target.value)}
                    />
                )}

                {/* DURATION */}

                {/* CONFIRM */}
                <button
                    className="sf-quick-create"
                    disabled={saving || (mode === "booking" && selectedServices.length === 0)}
                    onClick={handleConfirm}
                >
                    {mode === "booking"
                        ? translations[language].calendar.walkin.footer.confirm
                        : language === "es"
                        ? "Bloquear tiempo"
                        : "Block time"}
                </button>

                {/* FULL MODAL */}
                {/* <button
                    className="sf-quick-more"
                    onClick={() => {
                        onClose();
                        onSuccess("openFull");
                    }}
                >
                    {translations[language].calendar.walkin.moreOptions}
                </button> */}
            </div>
        </div>
    );
};

export default AddBookingPopup;
